import type { Editor, Range } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

import { findSuggestionMatch } from './findSuggestionMatch';

export interface SuggestionKeyDownProps {
  view: EditorView;
  event: KeyboardEvent;
  range: Range;
}

export interface SuggestionOptions {
  editor: Editor;
}

type SuggestState = {
  active: boolean;
  range: Range;
  query: null | string;
  text: null | string;
  decorationId?: string | null;
};

export const mentionPluginKey = new PluginKey<SuggestState>('mention');

export function Suggestion(args: SuggestionOptions) {
  const { editor } = args;
  const plugin = new Plugin({
    key: mentionPluginKey,

    state: {
      init() {
        const state: SuggestState = {
          range: {
            from: 0,
            to: 0,
          },
          query: null,
          text: null,
          active: false,
        };

        return state;
      },

      apply(transaction, prev, _oldState, state) {
        const { isEditable } = editor;
        const { selection } = transaction;
        const { empty } = selection;
        const next = { ...prev };

        if (isEditable && (empty || editor.view.composing)) {
          const match = findSuggestionMatch({
            $position: selection.$from,
          });

          const decorationId = `id_${Math.floor(Math.random() * 0xffffffff)}`;

          // If we found a match, update the current state to show it
          if (match) {
            next.active = true;
            next.decorationId = prev.decorationId ? prev.decorationId : decorationId;
            next.range = match.range;
            next.query = match.query;
            next.text = match.text;
          } else {
            next.active = false;
          }
        } else {
          next.active = false;
        }

        // Make sure to empty the range if suggestion is inactive
        if (!next.active) {
          next.decorationId = null;
          next.range = { from: 0, to: 0 };
          next.query = null;
          next.text = null;
        }

        return next;
      },
    },
  });

  return plugin;
}
